import { useState } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
  Avatar,
} from '@mui/material'
import {
  Search,
  EmojiEvents,
  Lock,
  CheckCircle,
  Star,
  Explore,
  Nature,
  School,
  LocalActivity,
  Groups,
  Computer,
  Palette,
  FitnessCenter,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'

interface Badge {
  id: string
  name: string
  description: string
  category: string
  icon: string
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  points: number
  earned: boolean
  earnedDate?: string
  progress?: number
  maxProgress?: number
  imageUrl?: string
}

const badgeCategories = [
  { id: 'all', label: 'All Badges', icon: <EmojiEvents /> },
  { id: 'nature', label: 'Nature', icon: <Nature /> },
  { id: 'urban', label: 'Urban Explorer', icon: <Explore /> },
  { id: 'learning', label: 'Learning', icon: <School /> },
  { id: 'social', label: 'Social', icon: <Groups /> },
  { id: 'creative', label: 'Creative', icon: <Palette /> },
  { id: 'tech', label: 'Tech', icon: <Computer /> },
  { id: 'lifestyle', label: 'Lifestyle', icon: <LocalActivity /> },
  { id: 'health', label: 'Health', icon: <FitnessCenter /> },
]

// Mock badge data
const mockBadges: Badge[] = [
  {
    id: '1',
    name: 'First Steps',
    description: 'Complete your first adventure',
    category: 'learning',
    icon: '🎯',
    rarity: 'common',
    points: 10,
    earned: true,
    earnedDate: '2025-09-15',
    imageUrl: '/assets/images/Quick_Badges_Nature.png',
  },
  {
    id: '2',
    name: 'Nature Enthusiast',
    description: 'Complete 5 nature-themed adventures',
    category: 'nature',
    icon: '🌲',
    rarity: 'rare',
    points: 50,
    earned: true,
    earnedDate: '2025-09-20',
    progress: 5,
    maxProgress: 5,
    imageUrl: '/assets/images/Reward_Badges_Nature.png',
  },
  {
    id: '3',
    name: 'Urban Legend',
    description: 'Discover 10 hidden urban locations',
    category: 'urban',
    icon: '🏙️',
    rarity: 'epic',
    points: 100,
    earned: false,
    progress: 7,
    maxProgress: 10,
    imageUrl: '/assets/images/Quick_Badges_Urban.png',
  },
  {
    id: '4',
    name: 'Social Butterfly',
    description: 'Join 3 group adventures',
    category: 'social',
    icon: '👥',
    rarity: 'common',
    points: 25,
    earned: true,
    earnedDate: '2025-09-18',
    imageUrl: '/assets/images/Reward_Badges_Social.png',
  },
  {
    id: '5',
    name: 'History Scholar',
    description: 'Complete all historical trail adventures',
    category: 'learning',
    icon: '📚',
    rarity: 'legendary',
    points: 250,
    earned: false,
    progress: 4,
    maxProgress: 8,
    imageUrl: '/assets/images/Reward_Badges_Learning.png',
  },
  {
    id: '6',
    name: 'Creative Mind',
    description: 'Create 5 unique adventures',
    category: 'creative',
    icon: '🎨',
    rarity: 'rare',
    points: 75,
    earned: false,
    progress: 2,
    maxProgress: 5,
    imageUrl: '/assets/images/Reward_Badges_Creative.png',
  },
  {
    id: '7',
    name: 'Tech Savvy',
    description: 'Use all available digital tools',
    category: 'tech',
    icon: '💻',
    rarity: 'rare',
    points: 60,
    earned: true,
    earnedDate: '2025-09-22',
    imageUrl: '/assets/images/Reward_Badges_Tech.png',
  },
  {
    id: '8',
    name: 'Early Riser',
    description: 'Complete 3 adventures before 9 AM',
    category: 'lifestyle',
    icon: '☀️',
    rarity: 'common',
    points: 30,
    earned: false,
    progress: 1,
    maxProgress: 3,
    imageUrl: '/assets/images/Reward_Badges_Lifestyle.png',
  },
  {
    id: '9',
    name: 'Marathon Runner',
    description: 'Walk 50 miles across all adventures',
    category: 'health',
    icon: '🏃',
    rarity: 'epic',
    points: 150,
    earned: false,
    progress: 32,
    maxProgress: 50,
    imageUrl: '/assets/images/Reward_Badges_Health.png',
  },
  {
    id: '10',
    name: 'Entertainment Elite',
    description: 'Attend 5 event-based adventures',
    category: 'lifestyle',
    icon: '🎭',
    rarity: 'rare',
    points: 80,
    earned: false,
    progress: 3,
    maxProgress: 5,
    imageUrl: '/assets/images/Reward_Badges_Entertainment.png',
  },
]

export default function BadgesPage() {
  usePageTitle('Badges')
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const { user } = useAuthStore()

  const filteredBadges = mockBadges.filter((badge) => {
    const matchesCategory = activeCategory === 'all' || badge.category === activeCategory
    const matchesSearch =
      searchQuery === '' ||
      badge.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      badge.description.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const earnedBadges = mockBadges.filter((b) => b.earned)
  const totalPoints = earnedBadges.reduce((sum, badge) => sum + badge.points, 0)
  const completionRate = Math.round((earnedBadges.length / mockBadges.length) * 100)

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return '#78909C'
      case 'rare':
        return '#42A5F5'
      case 'epic':
        return '#AB47BC'
      case 'legendary':
        return '#FFB300'
      default:
        return 'text.secondary'
    }
  }

  return (
    <Box>
      {/* Header with Stats */}
      <Box sx={{ mb: 4 }}>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center', border: '2px solid', borderColor: 'primary.main' }}>
              <EmojiEvents sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {earnedBadges.length}/{mockBadges.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Badges Earned
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center', border: '2px solid', borderColor: 'secondary.main' }}>
              <Star sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {totalPoints}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Points
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center', border: '2px solid', borderColor: 'success.main' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {completionRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Completion Rate
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionRate}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search badges..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Category Tabs */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeCategory}
          onChange={(_, newValue) => setActiveCategory(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {badgeCategories.map((category) => (
            <Tab
              key={category.id}
              value={category.id}
              label={category.label}
              icon={category.icon}
              iconPosition="start"
              sx={{ textTransform: 'none', minHeight: 60 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Badges Grid */}
      <Grid container spacing={3}>
        {filteredBadges.map((badge) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={badge.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                opacity: badge.earned ? 1 : 0.7,
                border: '2px solid',
                borderColor: badge.earned ? getRarityColor(badge.rarity) : 'divider',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              {/* Badge Icon/Image */}
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: badge.earned ? 'background.paper' : 'action.hover',
                  position: 'relative',
                }}
              >
                {badge.earned ? (
                  <Box sx={{ position: 'relative' }}>
                    <Typography sx={{ fontSize: 80 }}>{badge.icon}</Typography>
                    <CheckCircle
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        color: 'success.main',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        fontSize: 28,
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative', opacity: 0.3 }}>
                    <Typography sx={{ fontSize: 80 }}>{badge.icon}</Typography>
                    <Lock
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        color: 'text.disabled',
                        fontSize: 28,
                      }}
                    />
                  </Box>
                )}
              </Box>

              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Chip
                    label={badge.rarity}
                    size="small"
                    sx={{
                      backgroundColor: getRarityColor(badge.rarity),
                      color: 'white',
                      fontWeight: 'bold',
                      textTransform: 'capitalize',
                    }}
                  />
                  <Chip label={`${badge.points} pts`} size="small" variant="outlined" />
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {badge.name}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {badge.description}
                </Typography>

                {/* Progress Bar for unearned badges */}
                {!badge.earned && badge.maxProgress && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {badge.progress}/{badge.maxProgress}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(badge.progress! / badge.maxProgress) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}

                {/* Earned Date */}
                {badge.earned && badge.earnedDate && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    Earned on {new Date(badge.earnedDate).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredBadges.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No badges found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or category filter
          </Typography>
        </Paper>
      )}
    </Box>
  )
}
